import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AgentVerificationCheck } from '@percent/admin-dashboard/api/actions/validationSubmissions/validationSubmissions.types'
import { AgentVerificationStatus } from '@percent/admin-dashboard/api/types'
import { AgentVerificationStatus as BEAgentVerificationStatus } from '@percent/domain/verifications'
import { ErrorView, Loader } from '@percent/admin-dashboard/common/components'
import { OrganisationRelationships } from '@percent/admin-dashboard/common/components/organisationRelationship/OrganisationRelationships'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { CardDashboard, Flex, Spacer, Tab, Tabs } from '@percent/lemonade'
import { useQuery, useQueryList } from '@percent/utility'
import { AgentVerificationDetailsProps, AgentVerificationModal } from './AgentVerificationDetails.types'
import { AgentVerificationDecisionContext } from './agentVerificationDecisionContext/AgentVerificationDecisionContext'
import { AgentDetails } from './components/agentDetails/AgentDetails'
import { AgentDetailsTopBar } from './components/agentDetailsTopBar/AgentDetailsTopBar'
import { AgentVerificationDecision } from './components/agentVerificationDecision/AgentVerificationDecision'
import { AgentVerificationDocuments } from './components/agentVerificationDocuments/AgentVerificationDocuments'
import { AgentVerificationMatchedDocuments } from './components/agentVerificationMatchedDocuments/AgentVerificationMatchedDocuments'
import { AgentVerificationOrganisationDetails } from './components/agentVerificationOrganisationDetails/AgentVerificationOrganisationDetails'
import { AgentVerificationSimilar } from './components/agentVerificationSimilar/AgentVerificationSimilar'
import { AgentVerificationTimeline } from './components/agentVerificationTimeline/AgentVerificationTimeline'
import { DomainVerification } from './components/domainVerification/DomainVerification'
import { KycAnalysis } from './components/kycVerification/KycAnalysis'
import { useRequiredParams, useValidationSubmissionsAccess } from '@percent/admin-dashboard/common/hooks'
import { getGroupedFraudChecks } from '@percent/admin-dashboard/common/components/fraudChecks/utils/getGroupedFraudChecks'
import { getGroupedFailedChecksCount } from '@percent/admin-dashboard/common/components/fraudChecks/utils/getGroupedFailedChecksCount'
import { getFailedChecksCount } from '@percent/admin-dashboard/common/components/fraudChecks/utils/getFailedChecksCount'
import { FraudFlagsPanel } from '@percent/admin-dashboard/common/components/fraudChecks/fraudFlagsPanel/FraudFlagsPanel'
import { getDeterministicRoleCategories } from './utils/getDeterministicRoleCategories'
import { isDetailsMatchingDeterministic } from './utils/getDetailsMatchingStep'
import { RoleCategoriesContext } from './roleCategoriesContext/RoleCategoriesContext'
import { DeterministicDetailsMatchingContext } from './deterministicDetailsMatchingContext/DeterministicDetailsMatchingContext'
import { FraudCheckDomainAnalysis } from '@percent/admin-dashboard/common/components/fraudChecks/fraudCheckDomainAnalysis/FraudCheckDomainAnalysis'

export function AgentVerificationDetails({
  approveEmail,
  data,
  errorMessage,
  isLoading,
  orgData,
  refresh
}: Readonly<AgentVerificationDetailsProps>) {
  const { id } = useRequiredParams()
  const { adminService, agentVerificationService, validationSubmissionsService } = useServices()
  const autoFraudChecksFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_AUTO_FRAUD_CHECKS)
  const { t } = useTranslation()
  // The documents on this page are the linked validation submission's documents, so they
  // are gated by the validation_submissions domain.
  const { canReadDocuments } = useValidationSubmissionsAccess()

  const [activeTab, setActiveTab] = useState(`/agent-verification/${id}`)
  const [openedModal, setOpenedModal] = useState<AgentVerificationModal | undefined>(undefined)
  const [userSelectedRightPanelTab, setUserSelectedRightPanelTab] = useState<string | null>(null)

  const [
    { data: validationSubmissionDocuments, isLoading: isLoadingValidationSubmissionDocuments },
    { refresh: refreshAgentVerificationDocuments }
  ] = useQueryList(
    ['/validation-submission-documents', data?.validationSubmissionId],
    adminService.getValidationSubmissionDocuments,
    { validationSubmissionId: data?.validationSubmissionId },
    { enabled: !!data?.validationSubmissionId && canReadDocuments }
  )

  const {
    data: agentVerificationLogs,
    isPending: isLoadingAgentVerificationLogs,
    refetch: refreshAgentVerificationLogs
  } = useQuery(['/agent-verifications', id, '/logs'], agentVerificationService.getAgentVerificationLogs, { id })

  const { data: agentVerificationSimilar, isPending: isLoadingAgentVerificationSimilar } = useQuery(
    ['/agent-verifications', id, '/similar'],
    agentVerificationService.getAgentVerificationSimilar,
    { id }
  )

  const { data: matchedDocumentsResponse, isPending: isLoadingAgentVerificationMatchedDocuments } = useQuery(
    ['/agent-verifications', id, '/matched-documents'],
    agentVerificationService.getAgentVerificationMatchedDocuments,
    { id },
    { shallowData: true }
  )

  const agentVerificationMatchedDocuments = matchedDocumentsResponse?.data ?? null
  const matchedDocumentsTotalCount =
    matchedDocumentsResponse?.totalCount ?? agentVerificationMatchedDocuments?.length ?? 0

  const { data: validationSubmissionConfig } = useQuery(
    ['/validation-submission-configurations', data?.configurationId],
    validationSubmissionsService.getValidationSubmissionConfiguration,
    { id: data?.configurationId },
    { enabled: !!data?.configurationId && !!autoFraudChecksFeatureFlag }
  )
  const { data: fraudCheckResults, refetch: fraudCheckResultsRefresh } = useQuery(
    ['/agent-verifications', id, '/fraud-check-results'],
    agentVerificationService.requestFraudCheckResult,
    { id }
  )
  const { data: domainVerification } = useQuery(
    ['/agent-verifications', id, '/domain-verification'],
    agentVerificationService.getAgentVerificationDomainVerification,
    { id }
  )
  const { data: kycVerification } = useQuery(
    ['/agent-verifications', id, '/kyc-verification'],
    agentVerificationService.getAgentVerificationKycVerification,
    { id }
  )

  const groupedFraudChecks = useMemo(() => getGroupedFraudChecks(fraudCheckResults), [fraudCheckResults])
  const agentVerificationContextValue = useMemo(
    () => ({
      isApproveModalOpened: openedModal === AgentVerificationModal.APPROVE,
      isRejectModalOpened: openedModal === AgentVerificationModal.REJECT,
      isApproveEmailModalOpened: openedModal === AgentVerificationModal.APPROVE_EMAIL,
      isUpdateStatusModalOpened: openedModal === AgentVerificationModal.UPDATE_STATUS,
      isMarkAsSafeModalOpened: openedModal === AgentVerificationModal.MARK_AS_SAFE,
      setOpenedModal
    }),
    [openedModal]
  )

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  if (isLoading) {
    return <Loader />
  }

  const isPending =
    !!data && [AgentVerificationStatus.PENDING, AgentVerificationStatus.PENDING_REVIEW].includes(data?.status)
  // Whether fraud checking ran is read from the application's own fraud check results,
  // not from the validation submission configuration.
  const isAutoFraudCheckEnabled =
    !!autoFraudChecksFeatureFlag && !!fraudCheckResults && fraudCheckResults.checkResults.length > 0

  const hasAutoVerifiedLog = !!agentVerificationLogs?.some(log => log.type === 'domain_auto_verified')
  const hasOrgEmailChange = !!agentVerificationLogs?.some(log => log.type === 'organisation_email_change')
  const hasOrgDomainChange = !!agentVerificationLogs?.some(log => log.type === 'organisation_domain_change')
  const hasInboxVerificationEmailSentLog = !!agentVerificationLogs?.some(
    log => log.type === 'inbox_verification_email_sent'
  )

  const areDomainsAutoApproved = hasAutoVerifiedLog && !hasOrgEmailChange && !hasOrgDomainChange
  const areDocumentsLoading = isLoading || (!!data?.validationSubmissionId && isLoadingValidationSubmissionDocuments)

  const autoApprovedSources = domainVerification?.result?.domains?.map(domain => domain.source) ?? []
  const isOrgDataAvailable = !!orgData
  const hasFailingFraudFlags = getFailedChecksCount(fraudCheckResults) > 0
  const showFraudFlagsPanel = isAutoFraudCheckEnabled && hasFailingFraudFlags
  const isMarkedAsSafe = !!fraudCheckResults?.isMarkedAsSafe

  const defaultRightPanelTab = isMarkedAsSafe && canReadDocuments ? 'documents' : 'fraud-flags'
  const rightPanelTab = userSelectedRightPanelTab ?? defaultRightPanelTab
  const documentsCount = validationSubmissionDocuments?.length ?? 0
  const fraudFlagsCount = getGroupedFailedChecksCount(groupedFraudChecks)

  const rightPanelTabs = [
    ...(canReadDocuments ? [{ key: 'documents', label: `${t('typography.documents')} (${documentsCount})` }] : []),
    { key: 'fraud-flags', label: `${t('autoFraudChecks.fraudFlags.title')} (${fraudFlagsCount})` }
  ]

  const roleCategoriesConfig = getDeterministicRoleCategories(validationSubmissionConfig)
  const isDeterministicDetailsMatching = isDetailsMatchingDeterministic(validationSubmissionConfig)

  const fullName = `${data?.firstName} ${data?.lastName}`

  const agentVerificationDocumentCard = canReadDocuments && (
    <AgentVerificationDocuments
      agentVerificationId={data?.id}
      orgType={orgData?.type}
      agentVerificationLogs={agentVerificationLogs}
      isLoadingAgentVerificationLogs={isLoadingAgentVerificationLogs}
      refreshAgentVerificationLogs={refreshAgentVerificationLogs}
      refreshAgentVerificationData={refresh}
      refreshAgentVerificationDocuments={refreshAgentVerificationDocuments}
      isLoading={areDocumentsLoading}
      documents={validationSubmissionDocuments}
      agentEmail={data?.email}
      isOrgDataAvailable={isOrgDataAvailable}
      isStatusPending={isPending}
    />
  )

  return (
    <DeterministicDetailsMatchingContext.Provider value={isDeterministicDetailsMatching}>
      <RoleCategoriesContext.Provider value={roleCategoriesConfig}>
        <Flex direction="column" gap={24} mb={24}>
          <AgentVerificationDecisionContext.Provider value={agentVerificationContextValue}>
            <AgentVerificationDecision
              organisationName={orgData?.displayName || orgData?.name}
              agentFullName={fullName}
              refresh={refresh}
              fraudCheckResultsRefresh={fraudCheckResultsRefresh}
              agentStatus={data?.status}
              isLoading={isLoading}
              errorMessage={errorMessage}
              approveEmail={approveEmail}
              hasFailingFraudFlags={hasFailingFraudFlags}
              allowedOrganisations={data?.configuration?.allowedOrganisations?.types || []}
              partnerName={data?.partnerName ?? undefined}
              isStatusDynamic={data?.configuration?.isStatusDynamic}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              // TODO: change this amount to total count whenever BE extends the similar endpoint with pagination (POST-MVP)
              similarApplicationsAmount={agentVerificationSimilar?.length ?? 0}
              matchedDocumentsAmount={matchedDocumentsTotalCount}
              fraudCheckResults={fraudCheckResults}
              isAutoFraudCheckEnabled={isAutoFraudCheckEnabled}
              isOrgDataAvailable={isOrgDataAvailable}
              validationRequestId={data?.validationRequestId}
              hasInboxVerificationEmailSentLog={hasInboxVerificationEmailSentLog}
            />
          </AgentVerificationDecisionContext.Provider>
          <AgentDetailsTopBar
            agentVerification={data}
            agentVerificationLogs={agentVerificationLogs}
            partnerName={data?.partnerName ?? undefined}
          />
        </Flex>
        <Tab index={`/agent-verification/${id}`} value={activeTab}>
          <Flex
            data-testid="documents-preview-layout"
            direction={{ base: 'column', lg: 'row' }}
            gap={32}
            align={{ base: 'stretch', lg: 'start' }}
          >
            <Flex flex={1} direction="column">
              <AgentDetails
                agentVerification={data}
                agentVerificationStatus={data?.status}
                isAgentEmailAutoApproved={areDomainsAutoApproved}
                isOrgDataAvailable={isOrgDataAvailable}
                hasInboxVerificationEmailSentLog={hasInboxVerificationEmailSentLog}
              />
              <Spacer size={6} axis="vertical" />
              {!!orgData && (
                <>
                  <AgentVerificationOrganisationDetails
                    agentVerificationWebsite={data?.organisationWebsite}
                    agentVerification={data}
                    organisation={orgData}
                    refresh={refresh}
                    agentVerificationLogs={agentVerificationLogs}
                    autoApprovedSources={autoApprovedSources}
                    agentVerificationChecks={
                      validationSubmissionConfig?.configuration.checks.find(
                        check => check.type === 'agent_verification'
                      ) as AgentVerificationCheck
                    }
                  />
                  <Spacer size={6} axis="vertical" />
                </>
              )}
              <OrganisationRelationships relationships={orgData?.relationships} />
              <FraudCheckDomainAnalysis
                groupedFraudChecks={groupedFraudChecks}
                isAutoFraudCheckEnabled={isAutoFraudCheckEnabled}
                shouldHideAlerts
              />
              <DomainVerification domainVerification={domainVerification?.result} />
              <Spacer size={6} axis="vertical" />
              <KycAnalysis kyc={kycVerification} />
            </Flex>
            <Flex
              flex={1}
              direction="column"
              pos="sticky"
              top={80}
              mah="calc(100vh - 80px - 16px)"
              style={{ boxSizing: 'border-box', overflow: 'auto' }}
            >
              {showFraudFlagsPanel ? (
                <CardDashboard>
                  <Tabs items={rightPanelTabs} activeTab={rightPanelTab} onChangeTab={setUserSelectedRightPanelTab} />
                  <Spacer size={6} axis="vertical" />
                  {rightPanelTab === 'documents' && canReadDocuments && (
                    <AgentVerificationDocuments
                      agentVerificationId={data?.id}
                      orgType={orgData?.type}
                      agentVerificationLogs={agentVerificationLogs}
                      isLoadingAgentVerificationLogs={isLoadingAgentVerificationLogs}
                      refreshAgentVerificationLogs={refreshAgentVerificationLogs}
                      refreshAgentVerificationData={refresh}
                      refreshAgentVerificationDocuments={refreshAgentVerificationDocuments}
                      isLoading={areDocumentsLoading}
                      documents={validationSubmissionDocuments}
                      agentEmail={data?.email}
                      isOrgDataAvailable={isOrgDataAvailable}
                      isStatusPending={isPending}
                      withCard={false}
                    />
                  )}
                  {rightPanelTab === 'fraud-flags' && (
                    <FraudFlagsPanel
                      groupedFraudChecks={groupedFraudChecks}
                      isAutoFraudCheckEnabled={isAutoFraudCheckEnabled}
                      fraudScore={fraudCheckResults?.fraudScore}
                      withCard={false}
                    />
                  )}
                </CardDashboard>
              ) : (
                agentVerificationDocumentCard
              )}
            </Flex>
          </Flex>
        </Tab>
        <Tab index={`/agent-verification/${id}/timeline`} value={activeTab}>
          <AgentVerificationTimeline
            logs={agentVerificationLogs}
            isLoadingAgentVerificationLogs={isLoadingAgentVerificationLogs}
          />
        </Tab>
        <Tab index={`/agent-verification/${id}/other`} value={activeTab}>
          <AgentVerificationSimilar
            similarApplications={agentVerificationSimilar}
            isLoadingSimilarApplications={isLoadingAgentVerificationSimilar}
          />
        </Tab>
        <Tab index={`/agent-verification/${id}/duplicate-documents`} value={activeTab}>
          <AgentVerificationMatchedDocuments
            matchedDocuments={agentVerificationMatchedDocuments}
            totalCount={matchedDocumentsTotalCount}
            isLoadingMatchedDocuments={isLoadingAgentVerificationMatchedDocuments}
            thisApplication={{
              name: fullName,
              email: data?.email,
              organisationName: orgData?.displayName ?? orgData?.name,
              status: data?.status as unknown as BEAgentVerificationStatus | undefined
            }}
            thisApplicationDocuments={validationSubmissionDocuments ?? null}
          />
        </Tab>
      </RoleCategoriesContext.Provider>
    </DeterministicDetailsMatchingContext.Provider>
  )
}
